import { useEffect, type ReactNode } from 'react';
import { useOrg } from './queries';
import { useAuth } from '../auth/AuthProvider';
import { applyTheme, resolveTheme } from '../../lib/theme';

/**
 * Applies a tenant's branding at runtime.
 *
 * Every colour in the product resolves through a CSS custom property, so
 * switching org repaints without a rebuild and without a flash of the wrong
 * palette. What is not tenant-controlled is the status colours: red, amber and
 * green mean the same thing in every organisation, which is what lets a support
 * conversation about "the blocked ones" travel between tenants.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const { orgId } = useAuth();
  const { data: org } = useOrg(orgId);

  useEffect(() => {
    applyTheme(document.documentElement, resolveTheme(org?.branding));

    const title = org?.appName || org?.name;
    document.title = title ? `${title} · Trackilero` : 'Trackilero';
  }, [org]);

  // The favicon is a separate effect because replacing the link element on
  // every palette change makes some browsers flicker the tab icon.
  useEffect(() => {
    const favicon = org?.branding?.faviconUrl;
    if (!favicon) return;

    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = favicon;
  }, [org?.branding?.faviconUrl]);

  return <>{children}</>;
}
